// Lit toggles (qa-runs#371; shared/src/rules/starts.ts): a Toggle rule switched on for a token stays on — Rage,
// Fighting Defensively, a stance — until switched off or it ends by itself. The hot bar shows the token's lit ones as
// lit chips, each with its switch (`toggle.off`); the board rings the token while any is on (`toggleRing`). Decides
// nothing: it draws the toggles the server sent (a player only those on tokens they may see) and sends the switch.
import type { Toggles } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';

/** A token's lit toggles as the store holds them. */
export function litToggles(state: unknown, token: string): Toggles[string] {
  return ((state as { toggles?: Toggles }).toggles?.[token]) ?? [];
}

/** The ring the board draws round a token with a toggle on, or null: its colour and the names for its tooltip. */
export function toggleRing(state: unknown, token: string): { colour: string; title: string } | null {
  const lit = litToggles(state, token);
  return lit.length ? { colour: '#f59e0b', title: `On: ${lit.map((x) => x.entry.name).join(', ')}` } : null;
}

/** Switch a token's toggle on (by its entry's id) or off. The server stamps how it ends and spends its action. */
export function sendToggle(act: (body: never, onReply?: (r: { ok: boolean; error?: string }) => void) => void, token: string, entry: string, on: boolean, done?: (error: string | null) => void): void {
  act({ kind: on ? 'toggle.on' : 'toggle.off', token, entry } as never, (r) => done?.(r.ok ? null : r.error ?? 'refused'));
}

/** The hot bar's lit chips for a token, each with its switch off (for its owner and the DM). */
export function ToggleLights({ token, mayAct }: { token: string; mayAct: boolean }) {
  const { store, act, setNotice } = useTableState();
  const lit = litToggles(store.state, token);
  if (!lit.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" aria-label="Toggles on">
      {lit.map((x) => {
        const lasts = x.ends === 'encounter' ? 'until the end of the encounter' : x.ends === 'rounds' ? `for ${x.rounds} round${x.rounds === 1 ? '' : 's'} from round ${x.since ?? '?'}` : 'until switched off';
        return (
          <span key={x.entry.id} className="inline-flex items-center gap-1 rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900 shadow-[0_0_6px_#f59e0b] dark:bg-amber-900/60 dark:text-amber-100" title={`${x.entry.name}: on ${lasts}${x.action !== 'none' ? `; switching it is a ${x.action} action` : ''}`}>
            {x.entry.name}
            {mayAct && (
              <button type="button" className="rounded px-1 hover:bg-amber-200 dark:hover:bg-amber-800" aria-label={`Switch ${x.entry.name} off`}
                onClick={() => sendToggle(act as never, token, x.entry.id, false, (e) => { if (e) setNotice(e); })}>Off</button>
            )}
          </span>
        );
      })}
    </div>
  );
}
